import React, { useState } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Radio, Message } from '@getro/rombo';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useUpdateTalentNetworkMutation } from 'services/network';
import { activeNetworkSelector } from 'redux/selectors';
import { TalentDivider } from './talentDivider';
import { TalentSetting } from './components/talentSetting';

export const JobBoardTnSubscriptionSettings = ({ talentNetworkSettings }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const hasJobApplicationLinkedinOptionalFeature = activeNetwork?.features?.includes(
    'job_application_linkedin_optional',
  );

  const [values, setValues] = useState({
    askJbVisitorsToJoinTnBeforeLeavingToJobSource:
      talentNetworkSettings?.askJbVisitorsToJoinTnBeforeLeavingToJobSource ?? false,
    requireJbVisitorsToJoinTnBeforeLeavingToJobSource:
      talentNetworkSettings?.requireJbVisitorsToJoinTnBeforeLeavingToJobSource ?? false,
    askJobAlertSubscribersToJoinTn: talentNetworkSettings?.askJobAlertSubscribersToJoinTn ?? false,
    showJoinTnBlockToJobApplicants: talentNetworkSettings?.showJoinTnBlockToJobApplicants ?? false,
    jobApplicationLinkedinOptional: talentNetworkSettings?.jobApplicationLinkedinOptional ?? false,
  });

  const [updateTalentNetwork] = useUpdateTalentNetworkMutation();

  const update = async (updates) => {
    try {
      await updateTalentNetwork(updates).unwrap();
      toast('Changes saved successfully', { icon: 'success' });
    } catch (err) {
      const msg = err?.data?.message || 'Failed to save changes';
      toast(msg, { icon: 'error' });
    }
  };

  const setValueForField = (field, value) => {
    setValues({ ...values, [field]: value });
    update({ ...values, [field]: value });
  };

  return (
    <>
      {hasJobApplicationLinkedinOptionalFeature && (
        <>
          <Flex sx={{ flexDirection: 'column', gap: '40px' }}>
            <TalentSetting
              title="Job application settings"
              description="Control what information candidates provide when applying to jobs. This only applies to posted jobs using the on job board application method."
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Flex sx={{ flexDirection: 'column', rowGap: '4px' }}>
                <Text lineHeight="140%" fontSize="14px" fontWeight="500" as="h4">
                  LinkedIn profile URL on job applications
                </Text>
                <Text as="p" color="text.main" lineHeight="20px" fontSize="14px">
                  We recommend requiring LinkedIn to enable automatic profile enrichment and complete profiles in Getro.
                </Text>
              </Flex>
              <Flex sx={{ gap: '16px' }}>
                <Radio
                  name="jobApplicationLinkedinOptional"
                  checked={!values.jobApplicationLinkedinOptional}
                  label="Required (recommended)"
                  onChange={() => {
                    setValueForField('jobApplicationLinkedinOptional', false);
                  }}
                />
                <Radio
                  name="jobApplicationLinkedinOptional"
                  checked={values.jobApplicationLinkedinOptional}
                  label="Optional"
                  onChange={() => {
                    setValueForField('jobApplicationLinkedinOptional', true);
                  }}
                />
              </Flex>
              {values.jobApplicationLinkedinOptional && (
                <Message variant="info">
                  Without a LinkedIn URL, profiles cannot be automatically enriched. Only the information provided in
                  the application form will be available in Getro.
                </Message>
              )}
            </Box>
          </Flex>
          <TalentDivider />
        </>
      )}
      <Flex sx={{ flexDirection: 'column', gap: '40px' }}>
        <TalentSetting
          title="Capture job board visitor information"
          description="Turn anonymous visitors into enriched contacts. Understand your audience, measure the hires you help make, and grow your candidate database."
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TalentSetting
            toggleTalentNetwork={() => {
              setValueForField(
                'askJbVisitorsToJoinTnBeforeLeavingToJobSource',
                !values.askJbVisitorsToJoinTnBeforeLeavingToJobSource,
              );
            }}
            tnHidden={values.askJbVisitorsToJoinTnBeforeLeavingToJobSource}
            title="Ask for LinkedIn profile before visiting a job posting"
            description="Prompt visitors for their LinkedIn profile before they leave to the company's careers page."
          />
          {values.askJbVisitorsToJoinTnBeforeLeavingToJobSource && (
            <Flex sx={{ gap: '16px' }}>
              <Radio
                name="jbProfileRequirement"
                checked={values.requireJbVisitorsToJoinTnBeforeLeavingToJobSource}
                label="Required: Users must provide a profile"
                onChange={() => {
                  setValueForField('requireJbVisitorsToJoinTnBeforeLeavingToJobSource', true);
                }}
              />
              <Radio
                name="jbProfileRequirement"
                checked={!values.requireJbVisitorsToJoinTnBeforeLeavingToJobSource}
                label="Optional: Users can skip"
                onChange={() => {
                  setValueForField('requireJbVisitorsToJoinTnBeforeLeavingToJobSource', false);
                }}
              />
            </Flex>
          )}
        </Box>
        <TalentSetting
          toggleTalentNetwork={() => {
            setValueForField('askJobAlertSubscribersToJoinTn', !values.askJobAlertSubscribersToJoinTn);
          }}
          tnHidden={values.askJobAlertSubscribersToJoinTn}
          title="Invite job alert subscribers to join the Talent Network"
          description="Turn job alert subscribers into engaged candidates you can track and support."
        />
        <TalentSetting
          disabled={talentNetworkSettings.tnHidden}
          toggleTalentNetwork={() => {
            setValueForField('showJoinTnBlockToJobApplicants', !values.showJoinTnBlockToJobApplicants);
          }}
          tnHidden={values.showJoinTnBlockToJobApplicants && !talentNetworkSettings.tnHidden}
          title="Invite job applicants to complete their Talent Network profile"
          description="Show a message after users apply to a job, inviting them to complete their Talent Network profile. Only works when the Talent Network is enabled."
        />
      </Flex>
      <TalentDivider />
    </>
  );
};

JobBoardTnSubscriptionSettings.propTypes = {
  talentNetworkSettings: PropTypes.object.isRequired,
};
